import * as React from "react";
import { classnames } from "../../utils";
import * as styles from "./toggle.module.scss";

interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "type" | "role"> {
  /** Controlled state. Omit it to run uncontrolled off `defaultChecked`. */
  checked?: boolean | undefined;
  /** Initial state when running uncontrolled. */
  defaultChecked?: boolean | undefined;
  onChange?: ((checked: boolean) => void) | undefined;
}

export const Toggle = ({
  checked,
  defaultChecked,
  onChange,
  onKeyDown,
  className,
  ...otherProps
}: ToggleProps): JSX.Element => {
  const isControlled = checked !== undefined;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.checked);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    // A native checkbox only toggles on Space. Inside a <form> Enter would submit instead, so
    // swallow it and toggle — .click() flips the checkbox and fires the change event.
    if (event.key === "Enter" && !event.defaultPrevented) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  return (
    <input
      {...otherProps}
      // Passing both `checked` and `defaultChecked` makes React warn, so only ever send one.
      {...(isControlled ? { checked } : { defaultChecked: !!defaultChecked })}
      type="checkbox"
      role="switch"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={classnames({
        [`${className}`]: !!className,
        [styles.base]: true,
      })}
    />
  );
};
